/**
 * Offline Model Service
 * Provides unified model caching and retrieval across GPU, CPU, and Cosy servers
 * Integrates with the localhost auto-detection system
 */

import axios from 'axios';
import { db } from '../db';

// Cache configuration
const CACHE_CONFIG = {
  maxAgeHours: 1,          // Default cache age in hours
  maxEntries: 1000,        // Maximum cached model entries
  timeoutMs: 5000,         // API request timeout
  retryAttempts: 3,        // Number of retry attempts
  retryDelay: 1000,        // Base retry delay in ms
};

/**
 * Fetches models from a specific server and caches them as a single server entry
 * @param {string} serverType - 'gpu', 'cpu', or 'cosy'
 * @param {string} serverUrl - Full server URL (e.g., 'http://localhost:7111')
 * @param {Object} options - Optional configuration
 * @returns {Promise<Array>} Array of fetched models
 */
export const fetchAndCacheModelsFromServer = async (serverType, serverUrl, options = {}) => {
  const { timeout = CACHE_CONFIG.timeoutMs, retries = CACHE_CONFIG.retryAttempts } = options;

  try {
    console.log(`[MODEL_CACHE] Fetching models for ${serverType} from ${serverUrl}...`);

    // Determine API endpoint based on server type
    let endpoint;
    switch (serverType) {
      case 'gpu':
      case 'cpu':
        endpoint = '/api/generate/get-models';
        break;
      case 'cosy':
        endpoint = '/api/generate/get-cosyflows';
        break;
      default:
        throw new Error(`Unknown server type: ${serverType}`);
    }

    // Fetch models with timeout and abort signal
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await axios.get(`${serverUrl}${endpoint}`, {
        signal: controller.signal,
        timeout: timeout
      });

      clearTimeout(timeoutId);
      console.log(`[MODEL_CACHE] Response from fetching: `, response)

      // Handle different response formats from different server types
      let models;
      if (serverType === 'gpu' || serverType === 'cpu') {
        // GPU/CPU servers return { models: [...] }
        models = response.data.models || response.data;
      } else if (serverType === 'cosy') {
        // Cosy servers may return direct array or { data: [...] }
        models = response.data.data || response.data;
      } else {
        // Fallback - try both formats
        models = response.data.models || response.data;
      }

      if (!Array.isArray(models)) {
        throw new Error(`Invalid response format: expected array, got ${typeof models}`);
      }

      // Filter models for Cosy servers to only include installed cosyflows
      let filteredModels = models;
      if (serverType === 'cosy') {
        filteredModels = models.filter(cosyflow => cosyflow.install_status === 'INSTALLED');
        console.log(`[MODEL_CACHE] Filtered ${models.length} cosyflows to ${filteredModels.length} installed ones`);
      }

      // Extract model names from the filtered response
      const modelNames = filteredModels.map(model => extractModelName(model, serverType)).filter(Boolean);

      // Store as single server entry with models array
      const serverEntry = {
        serverType,
        serverUrl,
        models: modelNames,
        lastUpdated: new Date().toISOString()
      };

      // Check if server entry already exists
      const existingEntry = await db.offlineModels
        .where('serverType')
        .equals(serverType)
        .and(item => item.serverUrl === serverUrl)
        .first();

      if (existingEntry) {
        // Update existing entry
        await db.offlineModels.update(existingEntry.id, {
          models: modelNames,
          lastUpdated: new Date().toISOString()
        });
        console.log(`[MODEL_CACHE] ✅ Updated existing ${serverType} server with ${modelNames.length} models`);
      } else {
        // Create new entry
        await db.offlineModels.add(serverEntry);
        console.log(`[MODEL_CACHE] ✅ Created new ${serverType} server entry with ${modelNames.length} models`);
      }
      return models;

    } finally {
      clearTimeout(timeoutId);
    }

  } catch (error) {
    // Handle different error types
    if (error.name === 'AbortError') {
      console.warn(`[MODEL_CACHE] Request timeout for ${serverType} at ${serverUrl}`);
    } else if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      console.warn(`[MODEL_CACHE] Server not available: ${serverType} at ${serverUrl}`);
    } else {
      console.error(`[MODEL_CACHE] ❌ Failed to fetch models from ${serverType} at ${serverUrl}:`, error);
    }

    // Implement retry logic for network errors
    if (retries > 0 && (error.code === 'ECONNREFUSED' || error.name === 'AbortError')) {
      console.log(`[MODEL_CACHE] Retrying in ${CACHE_CONFIG.retryDelay}ms... (${retries} attempts left)`);
      await new Promise(resolve => setTimeout(resolve, CACHE_CONFIG.retryDelay));
      return fetchAndCacheModelsFromServer(serverType, serverUrl, {
        ...options,
        retries: retries - 1
      });
    }

    return [];
  }
};

/**
 * Extracts model name from different model object formats
 * @param {Object|string} model - Model object or string
 * @param {string} serverType - Server type for context
 * @returns {string} Extracted model name
 */
const extractModelName = (model, serverType) => {
  if (typeof model === 'string') {
    return model;
  }

  if (typeof model === 'object' && model !== null) {
    // Try different possible name fields
    const possibleNames = ['name', 'title', 'model_name', 'filename'];

    for (const field of possibleNames) {
      if (model[field] && typeof model[field] === 'string') {
        return model[field];
      }
    }

    // For cosy workflows, try nested data
    if (serverType === 'cosy' && model.data?.title) {
      return model.data.title;
    }

    // Fallback to JSON representation
    return JSON.stringify(model).substring(0, 50) + '...';
  }

  return null;
};

/**
 * Retrieves all cached models across all servers with optional filtering
 * @param {Object} options - Configuration options
 * @param {Object} options.activeServers - Currently detected servers (e.g., {gpu: 'http://localhost:7111', cosy: 'http://localhost:8190'})
 * @returns {Promise<Array>} Unified model list with server indicators
 */
export const getUnifiedOfflineModels = async (options = {}) => {
  const {
    maxAgeHours = CACHE_CONFIG.maxAgeHours,
    serverType = null,
    includeExpired = false,
    activeServers = null
  } = options;

  try {
    const cutoffTime = includeExpired
      ? new Date(0)
      : new Date(Date.now() - (maxAgeHours * 60 * 60 * 1000));

    let query = db.offlineModels.where('lastUpdated').above(cutoffTime.toISOString());

    // Filter by server type if specified
    if (serverType) {
      query = db.offlineModels
        .where('serverType')
        .equals(serverType)
        .and(item => new Date(item.lastUpdated) >= cutoffTime);
    }

    const serverEntries = await query.toArray();

    // Filter by currently active servers if provided
    const filteredEntries = activeServers
      ? serverEntries.filter(entry => {
          // Check if this server type is currently active
          const isServerActive = activeServers[entry.serverType];
          if (isServerActive) {
            console.log(`[MODEL_CACHE] Including models from active ${entry.serverType} server: ${entry.serverUrl}`);
            return true;
          } else {
            console.log(`[MODEL_CACHE] Filtering out models from inactive ${entry.serverType} server: ${entry.serverUrl}`);
            return false;
          }
        })
      : serverEntries;

    console.log(`[MODEL_CACHE] Server filtering: ${serverEntries.length} total entries → ${filteredEntries.length} active entries`);

    // Flatten models from filtered server entries
    const unifiedModels = [];
    const serverPriority = { gpu: 1, cpu: 2, cosy: 3 };

    filteredEntries.forEach(serverEntry => {
      if (serverEntry.models && Array.isArray(serverEntry.models)) {
        serverEntry.models.forEach(modelName => {
          unifiedModels.push({
            value: modelName,
            label: `${modelName} (${serverEntry.serverType.toUpperCase()})`,
            serverType: serverEntry.serverType,
            serverUrl: serverEntry.serverUrl,
            lastUpdated: serverEntry.lastUpdated,
            source: 'cache'
          });
        });
      }
    });

    // Deduplicate by model name (value), keeping the first occurrence
    const seen = new Set();
    const deduplicatedModels = unifiedModels.filter(model => {
        if (seen.has(model.value)) {
            return false;
        }
        seen.add(model.value);
        return true;
    });

    // Sort by server type priority (GPU > CPU > Cosy) then alphabetically
    deduplicatedModels.sort((a, b) => {
      const priorityDiff = serverPriority[a.serverType] - serverPriority[b.serverType];
      if (priorityDiff !== 0) return priorityDiff;
      return a.value.localeCompare(b.value);
    });

    console.log(`[MODEL_CACHE] Retrieved ${deduplicatedModels.length} unified models (${unifiedModels.length - deduplicatedModels.length} duplicates removed) from ${filteredEntries.length} servers`);
    return deduplicatedModels;

  } catch (error) {
    console.error('[MODEL_CACHE] Error getting unified offline models:', error);
    return [];
  }
};

/**
 * Gets cached models for a specific server type
 * @param {string} serverType - Server type to get models for
 * @param {Object} options - Configuration options
 * @returns {Promise<Array>} Server-specific model list
 */
export const getModelsForServer = async (serverType, options = {}) => {
  const { maxAgeHours = CACHE_CONFIG.maxAgeHours, includeExpired = false } = options;

  try {
    const cutoffTime = includeExpired
      ? new Date(0)
      : new Date(Date.now() - (maxAgeHours * 60 * 60 * 1000));

    const serverEntries = await db.offlineModels
      .where('serverType')
      .equals(serverType)
      .and(item => new Date(item.lastUpdated) >= cutoffTime)
      .toArray();

    // Flatten models from all matching server entries
    const serverModels = [];

    serverEntries.forEach(serverEntry => {
      if (serverEntry.models && Array.isArray(serverEntry.models)) {
        serverEntry.models.forEach(modelName => {
          serverModels.push({
            value: modelName,
            label: `${modelName} (${serverEntry.serverType.toUpperCase()})`,
            serverType: serverEntry.serverType,
            serverUrl: serverEntry.serverUrl,
            lastUpdated: serverEntry.lastUpdated,
            source: 'server_cache'
          });
        });
      }
    });

    console.log(`[MODEL_CACHE] Retrieved ${serverModels.length} models for ${serverType} from ${serverEntries.length} server entries`);
    return serverModels;

  } catch (error) {
    console.error(`[MODEL_CACHE] Error getting models for ${serverType}:`, error);
    return [];
  }
};

/**
 * Refreshes model cache for specific server or all servers
 * @param {string|null} targetServerType - Specific server type or null for all
 * @param {Object} detectedServers - Object mapping server types to URLs
 * @param {Object} options - Configuration options
 * @returns {Promise<Object>} Refresh results summary
 */
export const refreshModelCache = async (targetServerType = null, detectedServers = {}, options = {}) => {
  const { parallel = true, forceRefresh = false } = options;

  try {
    console.log(`[MODEL_CACHE] Starting cache refresh for ${targetServerType || 'all servers'}...`);

    const serversToRefresh = targetServerType
      ? { [targetServerType]: detectedServers[targetServerType] }
      : detectedServers;

    const refreshResults = {
      attempted: 0,
      successful: 0,
      failed: 0,
      errors: []
    };

    if (parallel) {
      // Refresh all servers in parallel
      const refreshPromises = Object.entries(serversToRefresh).map(
        async ([serverType, serverUrl]) => {
          if (serverUrl && ['gpu', 'cpu', 'cosy'].includes(serverType)) {
            refreshResults.attempted++;

            try {
              await fetchAndCacheModelsFromServer(serverType, serverUrl, options);
              refreshResults.successful++;
              return { serverType, status: 'success' };
            } catch (error) {
              refreshResults.failed++;
              refreshResults.errors.push({ serverType, error: error.message });
              return { serverType, status: 'failed', error: error.message };
            }
          }
          return null;
        }
      );

      await Promise.allSettled(refreshPromises);
    } else {
      // Refresh servers sequentially
      for (const [serverType, serverUrl] of Object.entries(serversToRefresh)) {
        if (serverUrl && ['gpu', 'cpu', 'cosy'].includes(serverType)) {
          refreshResults.attempted++;

          try {
            await fetchAndCacheModelsFromServer(serverType, serverUrl, options);
            refreshResults.successful++;
          } catch (error) {
            refreshResults.failed++;
            refreshResults.errors.push({ serverType, error: error.message });
          }
        }
      }
    }

    console.log(`[MODEL_CACHE] 🔄 Cache refresh completed: ${refreshResults.successful}/${refreshResults.attempted} servers updated`);

    // Clean up old cache entries for offline servers
    if (!targetServerType) {
      await cleanupOfflineServerCache(detectedServers);
    }

    return refreshResults;

  } catch (error) {
    console.error('[MODEL_CACHE] Error during cache refresh:', error);
    return { attempted: 0, successful: 0, failed: 0, errors: [error.message] };
  }
};

/**
 * Removes cached models for servers that are no longer detected
 * @param {Object} currentServers - Currently detected servers
 * @returns {Promise<number>} Number of removed entries
 */
export const cleanupOfflineServerCache = async (currentServers) => {
  try {
    const currentUrls = Object.values(currentServers).filter(Boolean);

    if (currentUrls.length === 0) {
      console.log('[MODEL_CACHE] No active servers to preserve, skipping cleanup');
      return 0;
    }

    const deletedCount = await db.offlineModels
      .where('serverUrl')
      .noneOf(currentUrls)
      .delete();

    if (deletedCount > 0) {
      console.log(`[MODEL_CACHE] 🧹 Cleaned up ${deletedCount} offline server cache entries`);
    }

    return deletedCount;

  } catch (error) {
    console.error('[MODEL_CACHE] Error cleaning up offline server cache:', error);
    return 0;
  }
};

/**
 * Provides default models when cache and API are unavailable
 * @param {string} serverType - Server type for defaults
 * @returns {Array} Default model list
 */
export const getDefaultModelsForServerType = (serverType) => {
  const defaults = {
    gpu: [
      { value: 'SDXL', label: 'SDXL (GPU - Default)', serverType: 'gpu', source: 'default' },
      { value: 'SD1.5', label: 'SD1.5 (GPU - Default)', serverType: 'gpu', source: 'default' },
      { value: 'SD2.1', label: 'SD2.1 (GPU - Default)', serverType: 'gpu', source: 'default' }
    ],
    cpu: [
      { value: 'SD1.5-CPU', label: 'SD1.5 (CPU - Default)', serverType: 'cpu', source: 'default' },
      { value: 'TinySD', label: 'TinySD (CPU - Default)', serverType: 'cpu', source: 'default' }
    ],
    cosy: [
      { value: 'Basic-Workflow', label: 'Basic Workflow (Cosy - Default)', serverType: 'cosy', source: 'default' },
      { value: 'Advanced-Workflow', label: 'Advanced Workflow (Cosy - Default)', serverType: 'cosy', source: 'default' }
    ]
  };

  return defaults[serverType] || [];
};

/**
 * Validates and repairs corrupted cache entries
 * @returns {Promise<Object>} Repair results
 */
export const validateAndRepairCache = async () => {
  const repairResults = {
    totalEntries: 0,
    corruptedEntries: 0,
    repairedEntries: 0,
    removedEntries: 0
  };

  try {
    console.log('[MODEL_CACHE] Starting cache validation and repair...');

    const allModels = await db.offlineModels.toArray();
    repairResults.totalEntries = allModels.length;

    const corruptedIds = [];
    const repairableIds = [];

    for (const model of allModels) {
      let isCorrupted = false;
      let canRepair = false;

      // Check for required fields
      if (!model.serverType || !model.modelName || !model.lastUpdated) {
        isCorrupted = true;

        // Check if we can repair
        if (model.serverType && model.modelData) {
          const extractedName = extractModelName(model.modelData, model.serverType);
          if (extractedName) {
            canRepair = true;
            repairableIds.push({
              id: model.id,
              repairs: {
                modelName: extractedName,
                lastUpdated: model.lastUpdated || new Date().toISOString()
              }
            });
          }
        }
      }

      // Check for invalid dates
      if (model.lastUpdated && isNaN(new Date(model.lastUpdated).getTime())) {
        isCorrupted = true;
        if (!canRepair) {
          canRepair = true;
          repairableIds.push({
            id: model.id,
            repairs: {
              lastUpdated: new Date().toISOString()
            }
          });
        }
      }

      if (isCorrupted && !canRepair) {
        corruptedIds.push(model.id);
      }
    }

    repairResults.corruptedEntries = corruptedIds.length + repairableIds.length;

    // Repair entries that can be fixed
    for (const repair of repairableIds) {
      try {
        await db.offlineModels.update(repair.id, repair.repairs);
        repairResults.repairedEntries++;
      } catch (error) {
        console.warn(`[MODEL_CACHE] Failed to repair entry ${repair.id}:`, error);
        corruptedIds.push(repair.id);
      }
    }

    // Remove irreparable entries
    if (corruptedIds.length > 0) {
      repairResults.removedEntries = await db.offlineModels.where('id').anyOf(corruptedIds).delete();
    }

    console.log(`[MODEL_CACHE] Cache validation completed:`, repairResults);
    return repairResults;

  } catch (error) {
    console.error('[MODEL_CACHE] Cache validation failed:', error);

    // Nuclear option - clear corrupt cache
    try {
      await db.offlineModels.clear();
      console.warn('[MODEL_CACHE] Cleared corrupted cache completely');
      return { ...repairResults, removedEntries: repairResults.totalEntries };
    } catch (clearError) {
      console.error('[MODEL_CACHE] Failed to clear corrupted cache:', clearError);
      return repairResults;
    }
  }
};

/**
 * Cleans up old cache entries based on age and count limits
 * @param {Object} options - Cleanup configuration
 * @returns {Promise<Object>} Cleanup results
 */
export const cleanupOldCacheEntries = async (options = {}) => {
  const {
    maxAgeHours = 24,
    maxEntries = CACHE_CONFIG.maxEntries,
    dryRun = false
  } = options;

  const cleanupResults = {
    totalEntries: 0,
    oldEntries: 0,
    excessEntries: 0,
    removedEntries: 0
  };

  try {
    const cutoffTime = new Date(Date.now() - (maxAgeHours * 60 * 60 * 1000));

    // Count total entries
    cleanupResults.totalEntries = await db.offlineModels.count();

    // Count old entries
    cleanupResults.oldEntries = await db.offlineModels
      .where('lastUpdated')
      .below(cutoffTime.toISOString())
      .count();

    // Calculate excess entries
    cleanupResults.excessEntries = Math.max(0, cleanupResults.totalEntries - maxEntries);

    if (!dryRun) {
      // Remove old entries
      if (cleanupResults.oldEntries > 0) {
        const removedOld = await db.offlineModels
          .where('lastUpdated')
          .below(cutoffTime.toISOString())
          .delete();

        cleanupResults.removedEntries += removedOld;
      }

      // Remove excess entries (oldest first)
      const remainingCount = await db.offlineModels.count();
      if (remainingCount > maxEntries) {
        const excess = remainingCount - maxEntries;
        const oldestEntries = await db.offlineModels
          .orderBy('lastUpdated')
          .limit(excess)
          .toArray();

        if (oldestEntries.length > 0) {
          const removedExcess = await db.offlineModels
            .where('id')
            .anyOf(oldestEntries.map(e => e.id))
            .delete();

          cleanupResults.removedEntries += removedExcess;
        }
      }
    }

    console.log(`[MODEL_CACHE] Cleanup ${dryRun ? 'simulation' : 'completed'}:`, cleanupResults);
    return cleanupResults;

  } catch (error) {
    console.error('[MODEL_CACHE] Cache cleanup failed:', error);
    return cleanupResults;
  }
};

/**
 * Retrieves server information for a specific model
 * @param {string} modelName - The model name to look up
 * @returns {Promise<Object|null>} Server info with serverType and serverUrl, or null if not found
 */
export const getServerForModel = async (modelName) => {
  try {
    const unifiedModels = await getUnifiedOfflineModels();
    const modelInfo = unifiedModels.find(model => model.value === modelName);

    if (modelInfo) {
      console.log(`[SERVER LOOKUP] Found server for model "${modelName}": ${modelInfo.serverType} at ${modelInfo.serverUrl}`);
      return {
        serverType: modelInfo.serverType,
        serverUrl: modelInfo.serverUrl
      };
    } else {
      console.warn(`[SERVER LOOKUP] No server found for model: ${modelName}`);
      return null;
    }
  } catch (error) {
    console.error(`[SERVER LOOKUP] Error looking up server for model "${modelName}":`, error);
    return null;
  }
};

/**
 * Gets cache statistics for monitoring and debugging
 * @returns {Promise<Object>} Cache statistics
 */
export const getCacheStatistics = async () => {
  try {
    const stats = {
      totalEntries: 0,
      entriesByServer: {},
      oldestEntry: null,
      newestEntry: null,
      totalSize: 0
    };

    const serverEntries = await db.offlineModels.toArray();
    stats.totalEntries = serverEntries.length;

    if (serverEntries.length > 0) {
      // Group by server type and count total models
      let totalModels = 0;
      serverEntries.forEach(serverEntry => {
        if (!stats.entriesByServer[serverEntry.serverType]) {
          stats.entriesByServer[serverEntry.serverType] = { servers: 0, models: 0 };
        }
        stats.entriesByServer[serverEntry.serverType].servers++;

        // Count models in this server
        if (serverEntry.models && Array.isArray(serverEntry.models)) {
          const modelCount = serverEntry.models.length;
          stats.entriesByServer[serverEntry.serverType].models += modelCount;
          totalModels += modelCount;
        }

        // Estimate size
        stats.totalSize += JSON.stringify(serverEntry).length;
      });

      stats.totalModels = totalModels;

      // Find oldest and newest entries
      const sortedByDate = serverEntries.sort((a, b) =>
        new Date(a.lastUpdated).getTime() - new Date(b.lastUpdated).getTime()
      );

      stats.oldestEntry = sortedByDate[0].lastUpdated;
      stats.newestEntry = sortedByDate[sortedByDate.length - 1].lastUpdated;
    }

    return stats;

  } catch (error) {
    console.error('[MODEL_CACHE] Error getting cache statistics:', error);
    return null;
  }
};

/**
 * Checks if offline servers are available by pinging the status endpoint
 * @param {number} timeout - Request timeout in ms (default: 2000)
 * @returns {Promise<{available: boolean, servers: Object}>} Availability status and detected servers
 */
export const checkOfflineServersAvailable = async (timeout = 2000) => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    const response = await fetch('http://localhost:7412/status', {
      signal: controller.signal
    });
    clearTimeout(timeoutId);

    if (!response.ok) {
      return { available: false, servers: {}, serverStatuses: {} };
    }

    const data = await response.json();

    if (!Array.isArray(data) || data.length === 0) {
      return { available: false, servers: {}, serverStatuses: {} };
    }

    // Parse server data from new format: [{name, url, status}]
    const servers = {};
    const serverStatuses = {};
    data.forEach(serverObj => {
      const { name: serverType, url: serverUrl, status: serverStatus } = serverObj;
      if (serverType && serverUrl) {
        serverStatuses[serverType] = serverStatus;
        // Only include 'ready' servers in the servers object for generation
        if (serverStatus === 'ready') {
          servers[serverType] = serverUrl;
        }
      }
    });

    return {
      available: Object.keys(servers).length > 0,
      servers,
      serverStatuses
    };
  } catch (error) {
    return { available: false, servers: {}, serverStatuses: {} };
  }
};

// Export cache configuration for external use
export { CACHE_CONFIG };